// Legal → Podepsané smlouvy (4. 11. 2026, zadání Ládi): přehled všech podepsaných smluv s klientkami
// a smluv, které čekají na podpis. Data: /api/smlouva-podpis?akce=prehled (jen owner).
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Download, Loader2, Search, Clock, UserRound, ChevronDown } from "lucide-react";
import Card from "../../components/Card.jsx";
import { ErrorNote, SkeletonRows } from "../../components/ui.jsx";
import { Cipy, Pil } from "../../components/sales/salesUi.jsx";
import { apiFetch } from "../../lib/api.js";

const API = "/api/smlouva-podpis";
const MESICE = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
const ZPUSOB = { tablet: "na místě", dalku: "na dálku", email: "e‑mailem na tabletu" };
const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

async function stahnoutPdf(id) {
  const r = await apiFetch(`${API}?akce=pdf&id=${id}`);
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j.pdf_base64) throw new Error(j.error || "PDF se nepodařilo stáhnout.");
  const bin = atob(j.pdf_base64);
  const pole = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) pole[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([pole], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = j.soubor || "Smlouva.pdf";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

function RadekSmlouvy({ s }) {
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const stahni = async () => {
    setBusy(true); setChyba("");
    try { await stahnoutPdf(s.id); } catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="py-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><FileText className="h-5 w-5" /></span>
          <div className="min-w-0">
            <div className="text-[15px] font-bold text-slate-900">{s.jmeno}</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <Pil ton="ok" mala casti={["podepsáno", s.podepsano]} />
              {s.balicek && <Pil ton="znacka" mala casti={["balíček", s.balicek]} />}
              <Pil ton="neutralni" mala casti={[ZPUSOB[s.rezim] || "na místě", s.trener]} />
              {s.verze ? <Pil ton="neutralni" mala casti={["verze", String(s.verze)]} /> : null}
              {!s.email_odeslan && <Pil ton="pozor" mala casti="e‑mail klientce neodešel" />}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex">
          {s.jmeno && (
            <Link to={`/clients?search=${encodeURIComponent(s.jmeno)}&open=1`} className="btn-secondary min-w-0 justify-center whitespace-nowrap px-2 text-[13px] sm:px-3 sm:text-sm">
              <UserRound className="h-4 w-4" />Karta klientky
            </Link>
          )}
          <button type="button" className="btn-secondary min-w-0 justify-center whitespace-nowrap px-2 text-[13px] sm:px-3 sm:text-sm" onClick={stahni} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Stáhnout PDF
          </button>
        </div>
      </div>
      {chyba && <div className="mt-2 text-[13px] font-semibold text-red-700">{chyba}</div>}
    </div>
  );
}

export default function PodepsaneSmlouvy() {
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState("");
  const [hledat, setHledat] = useState("");
  const [zpusob, setZpusob] = useState("vse");

  const nacti = () => {
    setChyba("");
    apiFetch(`${API}?akce=prehled`).then(async (r) => {
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Načtení se nepodařilo.");
      setData(j);
    }).catch((e) => setChyba(e.message));
  };
  useEffect(nacti, []);

  const podepsane = data?.podepsane || [];
  const cekajici = data?.cekajici || [];
  const vybrane = useMemo(() => {
    const q = norm(hledat.trim());
    return podepsane.filter((s) => (zpusob === "vse" || (zpusob === "dalku" ? s.rezim === "dalku" : s.rezim !== "dalku"))
      && (!q || norm([s.jmeno, s.trener, s.balicek].filter(Boolean).join(" ")).includes(q)));
  }, [podepsane, hledat, zpusob]);
  // Roky a v nich měsíce (od nejnovějších). Výchozí: otevřený jen nejnovější rok a v něm nejnovější měsíc.
  const roky = useMemo(() => {
    const m = new Map();
    for (const s of vybrane) {
      const k = s.mesic || String(s.podepsano_at).slice(0, 7); // YYYY-MM v pražském čase (server)
      const r = Number(k.slice(0, 4));
      if (!m.has(r)) m.set(r, new Map());
      const mm = m.get(r);
      if (!mm.has(k)) mm.set(k, { klic: k, nazev: MESICE[Number(k.slice(5, 7)) - 1], seznam: [] });
      mm.get(k).seznam.push(s);
    }
    return [...m.entries()].sort((a, b) => b[0] - a[0]).map(([rok, mm]) => ({ rok, mesice: [...mm.values()].sort((a, b) => (a.klic < b.klic ? 1 : -1)) }));
  }, [vybrane]);
  const [otevrene, setOtevrene] = useState(null); // Set klíčů roků a měsíců, null = výchozí
  const vychozi = useMemo(() => {
    const k = podepsane[0] ? (podepsane[0].mesic || String(podepsane[0].podepsano_at).slice(0, 7)) : null;
    return k ? new Set([k.slice(0, 4), k]) : new Set();
  }, [podepsane]);
  const hleda = !!hledat.trim() || zpusob !== "vse";
  const jeOtevreno = (k) => hleda || (otevrene || vychozi).has(k);
  const prepni = (k) => !hleda && setOtevrene((o) => { const n = new Set(o || vychozi); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  if (chyba) return <ErrorNote onRetry={nacti}>{chyba}</ErrorNote>;
  if (!data) return <SkeletonRows rows={6} />;

  return (
    <div className="space-y-4">
      {cekajici.length > 0 && (
        <Card lista ikona={Clock} title="Čeká na podpis" odznak={cekajici.length}>
          <div className="divide-y divide-slate-100">
            {cekajici.map((c) => (
              <div key={c.id} className="flex items-start gap-3 py-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600"><Clock className="h-5 w-5" /></span>
                <div className="min-w-0">
                  <div className="text-[15px] font-bold text-slate-900">{c.jmeno}</div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {c.plati_do && <Pil ton="pozor" mala casti={["odkaz platí do", c.plati_do]} />}
                    {c.balicek && <Pil ton="znacka" mala casti={["balíček", c.balicek]} />}
                    <Pil ton="neutralni" mala casti={[ZPUSOB[c.rezim] || "na místě", c.trener]} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card lista ikona={FileText} title="Podepsané smlouvy" odznak={podepsane.length}>
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label className="relative block flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input type="search" className="field w-full pl-9" placeholder="Hledat klientku nebo trenéra" value={hledat} onChange={(e) => setHledat(e.target.value)} />
            </label>
            <Cipy moznosti={[{ key: "vse", label: "Vše" }, { key: "tablet", label: "Na místě" }, { key: "dalku", label: "Na dálku" }]} value={zpusob} onChange={(v) => setZpusob(v || "vse")} />
          </div>
          {roky.map(({ rok, mesice }) => {
            const rOtevreno = jeOtevreno(String(rok));
            const pocet = mesice.reduce((a, m) => a + m.seznam.length, 0);
            return (
              <div key={rok} className="rounded-2xl border border-slate-200">
                <button type="button" aria-expanded={rOtevreno} onClick={() => prepni(String(rok))}
                  className="flex w-full items-center gap-2 rounded-2xl px-3 py-3 text-left hover:bg-slate-50 sm:px-4">
                  <span className="font-serif text-lg font-bold text-slate-900">{rok}</span>
                  <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[12px] font-bold text-brand-deep">{pocet} {pocet === 1 ? "smlouva" : pocet < 5 ? "smlouvy" : "smluv"}</span>
                  <ChevronDown className={`ml-auto h-5 w-5 text-slate-500 transition-transform ${rOtevreno ? "rotate-180" : ""}`} />
                </button>
                {rOtevreno && (
                  <div className="space-y-2 px-2 pb-2 sm:px-4 sm:pb-3">
                    {mesice.map((m) => {
                      const mOtevreno = jeOtevreno(m.klic);
                      return (
                        <div key={m.klic} className="rounded-xl bg-slate-50">
                          <button type="button" aria-expanded={mOtevreno} onClick={() => prepni(m.klic)}
                            className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left hover:bg-slate-100">
                            <span className="text-[14px] font-bold capitalize text-slate-800">{m.nazev}</span>
                            <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-slate-600">{m.seznam.length}</span>
                            <ChevronDown className={`ml-auto h-4 w-4 text-slate-500 transition-transform ${mOtevreno ? "rotate-180" : ""}`} />
                          </button>
                          {mOtevreno && <div className="divide-y divide-slate-100 rounded-b-xl bg-white px-2 sm:px-3">{m.seznam.map((s) => <RadekSmlouvy key={s.id} s={s} />)}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {vybrane.length === 0 && <div className="py-4 text-sm text-slate-500">{podepsane.length ? "Žádná smlouva neodpovídá hledání." : "Zatím žádná podepsaná smlouva."}</div>}
        </div>
      </Card>
    </div>
  );
}
